import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import FavoriteButton from "@/pages/detail/components/FavoriteButton";
import FavoritesPanel from "@/pages/detail/components/FavoritesPanel";
import { genreLabelToSlug, getMovieById, getRelated } from "@/utils/movie";
import { useSEO } from "@/hooks/useSEO";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);

  useSEO({
    title: movie ? `${movie.title} - 影视详情 - 欧美一级视频` : "影视详情 - 欧美一级视频",
    description: movie
      ? `${movie.title}（${movie.year}）是一部${movie.region}${movie.type === "movie" ? "电影" : "电视剧"}，类型包括${movie.genres.join("、")}。${movie.summary}`
      : "影视作品详情页",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},${movie.region}影视,欧美影视详情`
      : "欧美影视详情",
  });

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="max-w-md text-sm text-foreground-600">
          你访问的作品可能已被移除或链接有误，请返回首页浏览其他精选影视内容。
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
          <Link
            to="/category/movie"
            className="cursor-pointer rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50"
          >
            浏览电影分类
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(movie, 4);
  const metaList = [
    { label: "影视类型", value: movie.type === "movie" ? "电影" : "电视剧" },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "语言", value: movie.language },
    { label: "片长", value: movie.duration },
    { label: "上映时间", value: movie.releaseDate },
    { label: "评分", value: `${movie.rating.toFixed(1)} / 10` },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
      <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-foreground-600">
        <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link
          to={`/category/${movie.type}`}
          className="cursor-pointer hover:text-primary-600"
        >
          {movie.type === "movie" ? "欧美电影" : "欧美电视剧"}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{movie.title}</span>
      </nav>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col gap-6 p-4 md:flex-row md:gap-8 md:p-6">
          <div className="mx-auto w-40 shrink-0 md:mx-0 md:w-56">
            <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 影视详情`}
                className="h-full w-full object-top object-cover"
              />
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5 rounded-md bg-accent-50 px-3 py-2">
              <i className="ri-star-fill text-accent-500"></i>
              <span className="font-label text-lg font-black text-accent-700">
                {movie.rating.toFixed(1)}
              </span>
              <span className="text-xs text-foreground-500">/ 10</span>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-black text-foreground-950 md:text-4xl">
                {movie.title}
                <span className="ml-2 align-middle font-label text-base font-medium text-foreground-500 md:text-xl">
                  ({movie.year})
                </span>
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                  {movie.type === "movie" ? "电影" : "电视剧"}
                </span>
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${movie.type}/${genreLabelToSlug(g) ?? ""}`}
                    className="cursor-pointer rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900 hover:bg-secondary-200"
                  >
                    {g}
                  </Link>
                ))}
                <span className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
                  {movie.region}
                </span>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
              {movie.summary}
            </p>

            <dl className="grid grid-cols-1 gap-3 border-t border-background-200 pt-4 sm:grid-cols-2">
              {metaList.map((item) => (
                <div key={item.label} className="flex gap-2 text-sm">
                  <dt className="w-20 shrink-0 text-foreground-500">{item.label}</dt>
                  <dd className="flex-1 text-foreground-900">{item.value}</dd>
                </div>
              ))}
            </dl>

            <div className="pt-1">
              <FavoriteButton movieId={movie.id} />
            </div>
          </div>
        </div>

        <div className="border-t border-background-200 bg-background-100/60 p-4 md:p-6">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-4 w-1 rounded-full bg-accent-500"></span>
            剧情简介
          </h2>
          <p className="whitespace-pre-line text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
            {movie.description}
          </p>
        </div>
      </div>

      <FavoritesPanel />

      <section className="mt-10">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
          <span className="h-5 w-1 rounded-full bg-accent-500"></span>
          相关推荐
        </h2>
        {related.length === 0 ? (
          <p className="rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-10 text-center text-sm text-foreground-600">
            暂无相关推荐
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        )}
      </section>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3 border-t border-background-200 pt-6">
        <Link
          to={`/category/${movie.type}`}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50"
        >
          <i className="ri-arrow-left-line"></i>
          返回{movie.type === "movie" ? "电影" : "电视剧"}分类
        </Link>
        <Link
          to="/"
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i>
          回到首页
        </Link>
      </div>
    </div>
  );
}